import { useState, type FormEvent } from "react";
import { useInvites } from "./useInvites";

const ROLES = ["viewer", "operator", "tenant_admin"];

/** Tenant-admin panel: issue, list and revoke invites. */
export function InvitesPanel() {
  const { invites, loading, error, lastCreated, create, revoke, dismissLastCreated } = useInvites();
  const [username, setUsername] = useState("");
  const [role, setRole] = useState("operator");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    const ok = await create(username, role);
    setSubmitting(false);
    if (ok) setUsername("");
  };

  const inviteLink = lastCreated
    ? `${window.location.origin}/accept-invite?token=${encodeURIComponent(lastCreated.token)}`
    : null;

  return (
    <section aria-labelledby="invites-heading" className="card">
      <h2 id="invites-heading">Invites</h2>
      <p className="card__note">
        There is no email integration yet: relay the link below to the invitee yourself. It is shown
        only this once.
      </p>

      {lastCreated && inviteLink ? (
        <div className="invites__created" role="status">
          <p>
            Invited <strong>{lastCreated.username}</strong> as {lastCreated.role}. Send them this link:
          </p>
          <code className="invites__link">{inviteLink}</code>
          <button type="button" onClick={dismissLastCreated}>
            Dismiss
          </button>
        </div>
      ) : null}

      <form className="invites__form" onSubmit={(event) => void handleSubmit(event)}>
        <label className="field">
          <span>Username</span>
          <input
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>Role</span>
          <select value={role} onChange={(event) => setRole(event.target.value)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={submitting}>
          {submitting ? "Inviting…" : "Invite"}
        </button>
      </form>

      {error ? <p className="card__error">{error}</p> : null}

      {loading ? (
        <p className="card__note">Loading…</p>
      ) : invites.length === 0 ? (
        <p className="card__note">No pending invites.</p>
      ) : (
        <ul className="invites__list">
          {invites.map((invite) => (
            <li key={invite.invite_id} className="invites__item">
              <span>
                {invite.username} · {invite.role}
              </span>
              <button type="button" onClick={() => void revoke(invite.invite_id)}>
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
